import React, { useEffect, useState } from 'react'
import { useDebounce } from 'use-debounce'

import {
  CalculateProfitModal,
  CalculateProfitPricing,
  useAccordionState,
} from '@fourthwall/components'

import { useSubscription } from '../../../../../modules/Subscriptions'
import { getCosts, getCostsPerSize, getSizesPerCost } from '../../../../Product.utils'
import { useOffer } from '../../../../hooks/useOffer'
import type { PricingType } from '../../../SingleProduct.types'
import { useCustomizationQuery } from '../../../hooks/useCustomizationQuery'
import { DEFAULT_SOLD_VALUES, PRODUCT_TYPE_TITLE_COPY, SOLD_VALUE_DEBOUNCE_VALUE } from './consts'
import { useDraftPricingCalculationQuery } from './hooks/useDraftPricingCalculationQuery'
import {
  getDigitalCost,
  getEstimatedPaymentProcessingFees,
  getMOQPricingData,
  getOnDemandPricingData,
  getSoldValueError,
  getSuggestions,
  getUnitProfit,
  isSoldValueValid,
} from './utils'

export type Props = {
  productId: string
  initialPriceValue?: number
  pricingType: PricingType
  sizePriceSuggestion?: string
}

export const CalculateProfit = ({
  productId, // NOTE: Router useParams hook returns undefined in a modal.
  initialPriceValue = 0,
  pricingType,
  sizePriceSuggestion,
}: Props) => {
  const { offer } = useOffer(productId)

  const [includeFeesValue, setIncludeFeesValue] = useState(false)
  const [priceValue, setPriceValue] = useState(initialPriceValue.toString())
  const [soldValue, setSoldValue] = useState('')
  const [isSoldValueTouched, setIsSoldValueTouched] = useState(false)
  const [soldValueError, setSoldValueError] = useState<string | undefined>(undefined)
  const [soldValueDebounced] = useDebounce(soldValue, SOLD_VALUE_DEBOUNCE_VALUE)
  const { activeSubscription } = useSubscription()
  const digitalProductsFeePercentage = activeSubscription?.plan.digitalProductsFeePercent

  const productCosts = getCosts(offer.variants)
  const costsPerSize = getCostsPerSize(offer.variants)
  const sizesPerCost = getSizesPerCost(productCosts, costsPerSize) || []
  const { customization } = useCustomizationQuery(offer.customizationId)
  const minimumOrderQuantity = customization?.product.minimumOrdersNumber || 1
  const numberOfUnits = [minimumOrderQuantity, ...DEFAULT_SOLD_VALUES, Number(soldValueDebounced)]
  const feesValue = getEstimatedPaymentProcessingFees(Number(priceValue))
  const queryEnabled =
    offer.offerType === 'MOQ' &&
    !soldValueError &&
    (!isSoldValueTouched || isSoldValueValid(Number(soldValueDebounced), minimumOrderQuantity))
  const {
    draftPricingCalculation,
    draftPricingCalculationQuery: { isFetching },
  } = useDraftPricingCalculationQuery(
    offer.customizationId,
    { numberOfUnits },
    {
      keepPreviousData: true,
      enabled: queryEnabled,
    },
  )
  const { activeState, setActiveState, toggle } = useAccordionState([], {
    allowZeroActive: true,
    allowMultipleActive: false,
  })

  useEffect(() => {
    if (!activeState.length && draftPricingCalculation?.pricing.length) {
      setActiveState(Array(draftPricingCalculation?.pricing.length).fill(false))
    }
  }, [draftPricingCalculation])

  useEffect(() => {
    setSoldValueError(getSoldValueError(soldValueDebounced, minimumOrderQuantity))
  }, [soldValueDebounced])

  const handleAccordionChange = (id: number) => toggle(id)

  const handleSoldValueChange = (value: string) => {
    if (!isSoldValueTouched) {
      setIsSoldValueTouched(true)
    }

    setSoldValue(value)
  }

  const handleSellingPriceBlur = () => {
    if (!priceValue.length) {
      setPriceValue('0')
    }
  }

  const handleToggleIncludeFeesValue = () => setIncludeFeesValue((prevState) => !prevState)

  const getOnDemandContent = () => (
    <CalculateProfitPricing.OnDemandPricing
      includeFeesValue={includeFeesValue}
      data={getOnDemandPricingData(
        pricingType === 'EXPLICIT',
        includeFeesValue,
        Number(priceValue),
        sizesPerCost,
      )}
    />
  )

  const getMOQContent = () => (
    <CalculateProfitPricing.MOQPricing
      isLoading={isFetching}
      accordionState={activeState}
      includeFeesValue={includeFeesValue}
      data={getMOQPricingData(
        pricingType === 'EXPLICIT',
        includeFeesValue,
        Number(priceValue),
        sizesPerCost,
        draftPricingCalculation?.pricing,
      )}
      soldValue={soldValue}
      soldValueError={soldValueError}
      onAccordionChange={handleAccordionChange}
      onSoldValueChange={handleSoldValueChange}
    />
  )

  const getManualContent = () => {
    const cost =
      offer.manufacturingService === 'FOURTHWALL_BESPOKE' &&
      productCosts &&
      productCosts[0] !== null
        ? productCosts[0]
        : undefined

    return (
      <CalculateProfitPricing.ManualPricing
        includeFeesValue={includeFeesValue}
        feesValue={feesValue}
        cost={cost}
        unitProfit={getUnitProfit(Number(priceValue), cost || 0, includeFeesValue)}
      />
    )
  }

  const getDigitalContent = () => {
    const cost = getDigitalCost(Number(priceValue), digitalProductsFeePercentage)

    return (
      <CalculateProfitPricing.DigitalPricing
        includeFeesValue={includeFeesValue}
        feesValue={feesValue}
        cost={cost}
        digitalFeePercentage={digitalProductsFeePercentage}
        unitProfit={getUnitProfit(Number(priceValue), cost, includeFeesValue)}
      />
    )
  }

  const pricingContent = {
    ON_DEMAND: getOnDemandContent,
    MOQ: getMOQContent,
    MANUAL: getManualContent,
    DIGITAL: getDigitalContent,
  }

  return (
    <CalculateProfitModal.Root
      title={`Profit for ${PRODUCT_TYPE_TITLE_COPY[offer.offerType]} products`}
      content={
        <>
          <CalculateProfitModal.PriceInput
            priceValue={priceValue}
            onPriceChange={setPriceValue}
            onSellingPriceBlur={handleSellingPriceBlur}
          />
          {pricingContent[offer.offerType]()}
        </>
      }
      includeFeesValue={includeFeesValue}
      suggestions={getSuggestions(offer.offerType, sizePriceSuggestion)}
      onToggleIncludeFeesValue={handleToggleIncludeFeesValue}
    />
  )
}
